import type {Property} from "../../properties/types";

export const DISPATCH_LOG_SHEET_ID = "dispatch-log";

function urlFilesOnly(files: unknown): string[] {
  return Array.isArray(files) ? files.filter((file): file is string => typeof file === "string") : [];
}

function slimProperty(property: unknown) {
  if (!property || typeof property !== "object" || !("id" in property)) {
    return property;
  }
  const p = property as {id: number; name?: string};
  return {id: p.id, name: typeof p.name === "string" ? p.name : ""};
}

function slimHousehold(household: unknown) {
  if (!household || typeof household !== "object" || !("house_hold_id" in household)) {
    return household;
  }
  const h = household as {house_hold_id: number; name?: string};
  return {
    house_hold_id: h.house_hold_id,
    name: typeof h.name === "string" ? h.name : "",
  };
}

function slimDispatchType(dispatchType: unknown) {
  if (!dispatchType || typeof dispatchType !== "object") {
    return dispatchType;
  }
  const t = dispatchType as {label?: string; value?: number | string};
  let value = "";
  if (typeof t.value === "number" && !Number.isNaN(t.value)) {
    value = String(t.value);
  } else if (typeof t.value === "string") {
    value = t.value;
  }
  return {
    label: typeof t.label === "string" ? t.label : "",
    value,
  };
}

export function getDispatchLogFormDraftKey(
  isEditing: boolean,
  dispatchLogId?: number | null,
  contextPropertyId?: number | null,
) {
  if (isEditing && dispatchLogId != null) {
    return `dispatch-log:edit:${dispatchLogId}`;
  }
  if (contextPropertyId != null) {
    return `dispatch-log:create:p${contextPropertyId}`;
  }
  return "dispatch-log:create";
}

export function serializeDispatchLogFormDraftValues(values: Record<string, unknown>) {
  return {
    property: slimProperty(values.property),
    house_hold: slimHousehold(values.house_hold),
    dispatch_log_type: slimDispatchType(values.dispatch_log_type),
    name: values.name ?? "",
    callback_number: values.callback_number ?? "",
    details: values.details ?? "",
    send_notification: values.send_notification ?? false,
    create_automatic_dar: values.create_automatic_dar ?? false,
    files: urlFilesOnly(values.files),
  };
}

export function deserializeDispatchLogFormDraftValues(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const dispatchType = values.dispatch_log_type;
  let normalizedType = dispatchType;
  if (dispatchType && typeof dispatchType === "object" && "value" in dispatchType) {
    const t = dispatchType as {label?: string; value?: number | string};
    normalizedType = {
      label: typeof t.label === "string" ? t.label : "",
      value: t.value != null && t.value !== "" ? String(t.value) : "",
    };
  }
  return {
    ...values,
    dispatch_log_type: normalizedType,
    files: Array.isArray(values.files) ? values.files : [],
  };
}

type DispatchLogTypeOption = {label: string; value: string};

/** Reconcile slim draft type with live combobox options (fixes legacy drafts that stored value `0`). */
export function resolveDispatchLogDraftType(
  slimFromDraft: unknown,
  types: DispatchLogTypeOption[],
): DispatchLogTypeOption | null {
  if (!slimFromDraft || typeof slimFromDraft !== "object") {
    return null;
  }
  const t = slimFromDraft as {label?: string; value?: number | string};
  const valueStr = t.value != null && t.value !== "" ? String(t.value) : "";
  if (valueStr && valueStr !== "0") {
    return types.find((x) => x.value === valueStr) ?? {
      label: typeof t.label === "string" ? t.label : "",
      value: valueStr,
    };
  }
  if (typeof t.label === "string" && t.label) {
    return types.find((x) => x.label === t.label) ?? null;
  }
  return null;
}

/** Drafts store slim `{ id, name }`; merge back onto /me property. */
export function resolveDispatchLogDraftProperty(
  slimFromDraft: unknown,
  properties: Property[],
  fallback: Property,
): Property {
  if (!slimFromDraft || typeof slimFromDraft !== "object" || !("id" in slimFromDraft)) {
    return fallback;
  }
  const id = (slimFromDraft as {id: number}).id;
  return properties.find((p) => p.id === id) ?? fallback;
}

export function dispatchLogHasPersistableContent(values: Record<string, unknown>) {
  const name = typeof values.name === "string" ? values.name.trim() : "";
  const details = typeof values.details === "string" ? values.details.trim() : "";
  const callback =
    typeof values.callback_number === "string" ? values.callback_number.trim() : "";
  const files = Array.isArray(values.files) ? values.files : [];
  return (
    name.length > 0 ||
    details.length > 0 ||
    callback.length > 0 ||
    files.length > 0 ||
    values.house_hold != null ||
    values.dispatch_log_type != null ||
    values.send_notification === true ||
    values.create_automatic_dar === true
  );
}
